import { useState } from 'react';
import { api, ErrorApi } from '../services/api';
import { TecladoPin } from '../components/TecladoPin';
import { Aviso, Boton } from '../components/ui';

type Paso = 'actual' | 'nuevo' | 'repetir';

/**
 * Elegir un PIN propio.
 *
 * Es obligatoria la primera vez: el PIN provisorio viajó por WhatsApp, así que
 * mientras siga puesto el Worker bloquea todo lo demas (devuelve 428).
 */
export function CambiarPin({
  obligatorio,
  onListo,
  onCancelar,
}: {
  obligatorio: boolean;
  onListo: () => void;
  onCancelar?: () => void;
}) {
  const [paso, setPaso] = useState<Paso>('actual');
  const [actual, setActual] = useState('');
  const [nuevo, setNuevo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [intento, setIntento] = useState(0);

  function reiniciar(a: Paso, mensaje: string) {
    setError(mensaje);
    setPaso(a);
    setIntento((n) => n + 1);
  }

  async function recibir(pin: string) {
    setError(null);

    if (paso === 'actual') {
      setActual(pin);
      setPaso('nuevo');
      setIntento((n) => n + 1);
      return;
    }

    if (paso === 'nuevo') {
      if (pin === actual) {
        reiniciar('nuevo', 'El PIN nuevo tiene que ser distinto del actual.');
        return;
      }
      setNuevo(pin);
      setPaso('repetir');
      setIntento((n) => n + 1);
      return;
    }

    // paso === 'repetir'
    if (pin !== nuevo) {
      reiniciar('nuevo', 'Los dos PIN no coinciden. Probá de nuevo.');
      return;
    }

    setOcupado(true);
    try {
      await api.cambiarPin(actual, nuevo);
      onListo();
    } catch (e) {
      // El servidor tambien valida (PIN obvio, PIN actual incorrecto): su
      // mensaje es el que vale.
      reiniciar('actual', e instanceof ErrorApi ? e.message : 'No se pudo cambiar.');
      setOcupado(false);
    }
  }

  const etiquetas: Record<Paso, string> = {
    actual: obligatorio ? 'Ingresá el PIN que te pasaron' : 'Poné tu PIN actual',
    nuevo: 'Elegí tu PIN nuevo',
    repetir: 'Repetilo para confirmar',
  };

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center p-6">
      <header className="mb-8">
        <h1 className="text-[28px] leading-tight font-bold tracking-tight">Elegí tu PIN</h1>
        {obligatorio && (
          <p className="mt-2 text-base leading-normal">
            El PIN que te pasaron es provisorio. Ahora vas a elegir uno que solo conozcas vos.
          </p>
        )}
      </header>

      <TecladoPin
        onCompleto={recibir}
        ocupado={ocupado}
        etiqueta={etiquetas[paso]}
        paso={paso === 'actual' ? 1 : paso === 'nuevo' ? 2 : 3}
        reiniciar={intento}
      />

      {error && (
        <div className="mt-6">
          <Aviso tono="error" legible>{error}</Aviso>
        </div>
      )}

      {!obligatorio && onCancelar && (
        <div className="mt-8">
          <Boton tipo="secundario" ancho onClick={onCancelar}>
            Cancelar
          </Boton>
        </div>
      )}
    </main>
  );
}
